/**
 * Carte individuelle de notification (utilisée en mobile et desktop)
 */

import type {
  Notification,
  CommentNotification,
  StatusChangeNotification,
  InvoiceNotification,
  ProviderPendingNotification,
  InvoiceUploadedNotification,
} from '@/services/notification.service';
import { formatNotificationTime } from '../hooks/useNotifications';

interface NotificationItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
  onMarkAsRead?: (id: string) => void;
  variant: 'mobile' | 'desktop';
}

function isComment(n: Notification): n is CommentNotification { return n.type === 'comment'; }
function isInvoice(n: Notification): n is InvoiceNotification { return n.type === 'invoice'; }
function isStatusChange(n: Notification): n is StatusChangeNotification { return n.type === 'status_change'; }
function isProviderPending(n: Notification): n is ProviderPendingNotification {
  return n.type === 'provider_pending_validation';
}
function isInvoiceUploaded(n: Notification): n is InvoiceUploadedNotification {
  return n.type === 'invoice_uploaded';
}

function NotificationIcon({ notification }: { notification: Notification }) {
  const base = 'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center';
  if (isComment(notification)) return (
    <div className={`${base} bg-blue-100`}>
      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    </div>
  );
  if (isInvoice(notification)) return (
    <div className={`${base} bg-orange-100`}>
      <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </div>
  );
  if (isProviderPending(notification)) return (
    <div className={`${base} bg-amber-100`}>
      <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    </div>
  );
  if (isInvoiceUploaded(notification)) {
    const isLinked = notification.is_linked;
    return (
      <div className={`${base} ${isLinked ? 'bg-blue-100' : 'bg-orange-100'}`}>
        <svg className={`w-5 h-5 ${isLinked ? 'text-blue-600' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
    );
  }
  return (
    <div className={`${base} bg-purple-100`}>
      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
  );
}

function NotificationContent({ notification }: { notification: Notification }) {
  if (isComment(notification)) return (
    <>
      <p className="text-sm font-medium text-gray-900 mb-1">{notification.commenter_name}</p>
      <p className="text-sm text-gray-600 mb-1 line-clamp-2">{notification.comment_text}</p>
    </>
  );
  if (isInvoice(notification)) return (
    <>
      <p className="text-sm font-medium text-gray-900 mb-1">Facture {notification.invoice_number}</p>
      <p className="text-sm text-gray-600 mb-1">
        {notification.sender_name && `De ${notification.sender_name} - `}
        <span className="font-medium">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(notification.amount)}</span>
      </p>
    </>
  );
  if (isStatusChange(notification)) return (
    <>
      <p className="text-sm font-medium text-gray-900 mb-1">Changement de statut</p>
      <p className="text-sm text-gray-600 mb-1">
        <span className="font-medium">{notification.old_status_label}</span>
        {' → '}
        <span className="font-medium text-purple-600">{notification.new_status_label}</span>
      </p>
    </>
  );
  if (isProviderPending(notification)) return (
    <>
      <p className="text-sm font-medium text-gray-900 mb-1">🆕 Nouveau prestataire à valider</p>
      <p className="text-sm text-gray-600 mb-1">
        {notification.provider_name}
        {notification.provider_email && ` — ${notification.provider_email}`}
      </p>
    </>
  );
  if (isInvoiceUploaded(notification)) return (
    <>
      <p className="text-sm font-medium text-gray-900 mb-1">
        {notification.is_linked ? '📄 Nouvelle facture' : '⚠️ Facture non rattachée'}
      </p>
      <p className="text-sm text-gray-600 mb-1">
        De {notification.provider_name} —{' '}
        <span className="font-medium">
          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(notification.amount)}
        </span>
        {notification.project_name && <> · {notification.project_name}</>}
      </p>
    </>
  );
  return null;
}

export function NotificationItem({ notification, onClick, onMarkAsRead, variant }: NotificationItemProps) {
  const dotColor = isComment(notification)
    ? 'bg-blue-500'
    : isInvoice(notification)
    ? 'bg-orange-500'
    : isProviderPending(notification)
    ? 'bg-amber-500'
    : isInvoiceUploaded(notification)
    ? (notification.is_linked ? 'bg-blue-500' : 'bg-orange-500')
    : 'bg-purple-500';

  return (
    <div className="group relative">
      <button
        onClick={() => onClick(notification)}
        className={`w-full hover:bg-gray-50 transition-colors text-left ${
          variant === 'mobile' ? 'px-4 py-4' : 'px-4 py-3 pr-12'
        }`}
      >
        <div className="flex items-start gap-3">
          <NotificationIcon notification={notification} />
          <div className="flex-1 min-w-0">
            <NotificationContent notification={notification} />
            <div className="flex items-center gap-2 text-xs text-gray-500">
              {('project_name' in notification) && <span>{notification.project_name}</span>}
              {('content_title' in notification) && (
                <><span>•</span><span className="truncate">{notification.content_title}</span></>
              )}
              <span>•</span>
              <span>{formatNotificationTime(notification.created_at)}</span>
            </div>
          </div>
          {variant === 'desktop' && (
            <div className="flex-shrink-0 group-hover:hidden transition-opacity">
              <div className={`w-2 h-2 rounded-full ${dotColor}`} />
            </div>
          )}
        </div>
      </button>

      {variant === 'desktop' && onMarkAsRead && (
        <button
          onClick={(e) => { e.stopPropagation(); onMarkAsRead(notification.id); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white shadow-sm border border-gray-100 text-gray-400 hover:text-blue-600 hover:bg-blue-50 opacity-0 group-hover:opacity-100 transition-all duration-200 transform scale-90 group-hover:scale-100 z-10"
          title="Marquer comme lu"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </button>
      )}
    </div>
  );
}
